import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Check, FileSpreadsheet, Layers, Rows3, ScanLine, ShieldCheck, X } from 'lucide-react';
import { loadLaboratoryWork, loadLaboratoryWorkflowVersion, readLaboratoryQcForm, recordLaboratoryStage, resolveLaboratoryCode } from './api';
import { fileForUpload } from './qcResults';
import { ContainerSymbol } from './XomicsCollections';
import { StageGlyph } from './LaboratoryGlyphs';
import { FieldInput, payload } from './LaboratoryStageForm';
import { labError } from './LaboratoryParts';
import { LabScanContext, applyStageScan, readingsAverage, stageScanTarget, useLabScan } from './labScanContext';
import { fieldLabel, itemLabel, ownSampleTypeName, stageName, unitSymbol, workflowName } from './LaboratoryText';
import { Head, Pill } from './parts';
import { tx } from './i18n';
import './laboratory-workflows.css';
import './laboratory-receipt.css';
import './laboratory-batch.css';

const text = (key, values) => tx(`ui.lab.batch.${key}`, values);
const MEMORY = 'xomics-lab-batch-per-tube';
const empty = value => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
const STATES = { ready: 'flat', saving: 'info', recorded: 'ok', sent: 'warn', failed: 'bad', skipped: 'warn' };

// Which fields of a stage are filled in tube by tube. Measurements and the
// result differ per tube; the laboratory moves any other field over and its
// choice is kept for that stage on this computer.
export function perTubeFields(workflowCode, stage) {
  try {
    const kept = JSON.parse(window.localStorage.getItem(`${MEMORY}:${workflowCode}:${stage.code}`) || 'null');
    if (Array.isArray(kept)) return stage.fields.filter(field => kept.includes(field.code)).map(field => field.id);
  } catch (error) { /* the usual split is used */ }
  return stage.fields.filter(field => field.valueType === 'number' || field.role === 'outcome').map(field => field.id);
}

// What one tube's record holds: the shared entries, with its own on top.
export function rowValues(stage, shared, perTube, row) {
  const values = {};
  for (const field of stage.fields) {
    const value = perTube.includes(field.id) ? row.values[field.id] : shared[field.id];
    if (!empty(value)) values[field.id] = value;
  }
  return values;
}

// The required fields a tube's record still lacks, as field definitions.
export function rowNeeds(stage, values) {
  return stage.fields.filter(field => field.isRequired && empty(values[field.id]));
}

// What a filled receiving form says of a tube, as its row's own values: each measurement into the
// stage's number field of that code, and the overall QC status as the stage's result.
export function formValues(stage, entry) {
  const values = {};
  if (!entry) return values;
  for (const field of stage.fields) {
    if (field.valueType === 'number' && entry.values[field.code] !== undefined) values[field.id] = String(entry.values[field.code]);
    if (field.role === 'outcome' && entry.outcome && field.list) {
      const item = field.list.items.find(choice => choice.outcome === entry.outcome && choice.isActive);
      if (item) values[field.id] = item;
    }
  }
  return values;
}

export default function XomicsLabBatch() {
  const station = useContext(LabScanContext);
  const [batch, setBatch] = useState(null), [rows, setRows] = useState([]), [choice, setChoice] = useState(null);
  const [shared, setShared] = useState({}), [devices, setDevices] = useState([]), [note, setNote] = useState(''), [perTube, setPerTube] = useState([]);
  const [code, setCode] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState(''), [flash, setFlash] = useState(null);
  // What the last filled receiving form put in: how many tubes, those it could not, and what in it was no number.
  const [filled, setFilled] = useState(null), [reading, setReading] = useState(false);
  const formInput = useRef(null);
  const live = useRef({});
  live.current = { batch, rows, shared, devices, perTube };
  const queue = useRef(Promise.resolve());
  const stage = batch === null ? null : batch.stage;

  useEffect(() => {
    if (flash === null) return undefined;
    const timer = window.setTimeout(() => setFlash(null), 900);
    return () => window.clearTimeout(timer);
  }, [flash]);

  // Puts the cursor in a tube's first empty field of its own, so the next thing typed is that tube's reading.
  function focusRow(key) {
    window.setTimeout(() => {
      const { batch: current, perTube: own, rows: all } = live.current;
      const row = all.find(item => item.key === key);
      if (!current || !row) return;
      const field = current.stage.fields.filter(item => own.includes(item.id)).sort((a, b) => a.position - b.position).find(item => empty(row.values[item.id]));
      const target = field ? document.getElementById(`batch-${key}-${field.id}`) : null;
      if (target) target.focus();
      setFlash(key);
    }, 60);
  }

  function start(order, definition) {
    const found = definition.workflow.stages.find(item => item.id === order.currentStage.id);
    const next = { workflow: order.workflow, stage: found };
    live.current = { ...live.current, batch: next, perTube: perTubeFields(order.workflow.code, found) };
    setBatch(next); setPerTube(live.current.perTube); setShared({}); setDevices([]); setNote('');
  }

  function append(order, collection, scanned, entry = null) {
    const row = { key: order.uuid, order, collection: { code: collection.code, sampleType: collection.sampleType }, container: collection.container,
      scanned, values: formValues(live.current.batch.stage, entry), state: 'ready', error: '', retry: null };
    live.current = { ...live.current, rows: [...live.current.rows, row] };
    setRows(live.current.rows);
    focusRow(row.key);
    return { tone: 'ok', code: order.tube.printedCode ?? order.tube.code, title: text('added'), detail: ownSampleTypeName(collection.sampleType) };
  }

  // One tube into the batch: it must be waiting at the batch's stage. The first tube decides the stage.
  // From a filled receiving form (`entry`), the tube comes with its measurements.
  async function add(raw, scanned, entry = null) {
    const value = raw.trim();
    if (value === '') return null;
    setError('');
    // A tube scanned by its own code or by its label as printed (GH453369-01).
    const known = live.current.rows.find(row => row.order.tube.code === value || row.order.tube.printedCode === value);
    if (known) {
      if (scanned) mark(known.key);
      if (entry) update(known.key, current => ({ values: { ...current.values, ...formValues(live.current.batch.stage, entry) } }));
      focusRow(known.key); return { tone: 'ok', code: value, title: text('in_batch') };
    }
    try {
      const resolved = await resolveLaboratoryCode(value);
      const tube = resolved.selectedTubeUuid === null ? null : resolved.collection.tubes.find(item => item.uuid === resolved.selectedTubeUuid);
      if (!tube) return fail(resolved.collection.printedCode ?? resolved.collection.code, text('scan_tube_itself'));
      const again = live.current.rows.find(row => row.order.tube.uuid === tube.uuid);
      if (again) { if (scanned) mark(again.key); focusRow(again.key); return { tone: 'ok', code: tube.printedCode ?? tube.code, title: text('in_batch') }; }
      const work = await loadLaboratoryWork({ collectionUuid: resolved.collection.uuid, limit: 1 });
      const all = work.collections.length === 0 ? [] : work.collections[0].workOrders.filter(order => order.tube.uuid === tube.uuid);
      const orders = all.filter(order => order.canRecord && order.currentStage !== null);
      // A customer's sample waits for their answer after its first quality check.
      if (orders.length === 0 && all.some(order => order.status === 'awaiting_customer')) return fail(tube.printedCode ?? tube.code, text('waits_for_customer'));
      if (orders.length === 0) return fail(tube.printedCode ?? tube.code, text('no_work'));
      const collection = { ...resolved.collection, container: tube.container };
      if (live.current.batch === null) {
        if (orders.length > 1) { setChoice({ orders, collection, scanned, entry }); return { tone: 'ok', code: tube.printedCode ?? tube.code, title: text('which_workflow') }; }
        start(orders[0], await loadLaboratoryWorkflowVersion(orders[0].workflow.versionId));
        return append(orders[0], collection, scanned, entry);
      }
      const current = live.current.batch;
      const order = orders.find(item => item.workflow.versionId === current.workflow.versionId && item.currentStage.id === current.stage.id);
      if (!order) return fail(tube.printedCode ?? tube.code, text('other_stage'), `${orders[0].workflow.code} · ${stageName(orders[0].currentStage.code)}`);
      return append(order, collection, scanned, entry);
    } catch (caught) { return fail(value, labError(caught)); }
  }

  // A filled receiving form: each tube in it joins the batch with its measurements, one after another.
  async function fillFromForm(file) {
    setReading(true); setError(''); setFilled(null);
    try {
      const read = await readLaboratoryQcForm(await fileForUpload(file));
      const missed = [];
      for (const entry of read.rows) {
        const result = await enqueue(entry.tubeCode, false, entry);
        if (result && result.tone === 'bad') missed.push(`${entry.tubeCode}: ${result.title}${result.detail ? ` (${result.detail})` : ''}`);
      }
      setError('');
      setFilled({ added: read.rows.length - missed.length, missed, problems: read.problems.map(problem => text('form_row', { line: problem.line, problem: problem.message })) });
    } catch (caught) { setError(labError(caught)); } finally { setReading(false); }
  }
  function fail(label, title, detail) { setError(`${label} · ${title}${detail ? ` (${detail})` : ''}`); return { tone: 'bad', code: label, title, detail }; }
  function mark(key) { update(key, { scanned: true }); }
  function update(key, change) {
    live.current = { ...live.current, rows: live.current.rows.map(row => (row.key === key ? { ...row, ...(typeof change === 'function' ? change(row) : change) } : row)) };
    setRows(live.current.rows);
  }
  // Scans arrive faster than the look-ups answer; they are taken one after another.
  const enqueue = (value, scanned, entry = null) => {
    const turn = queue.current.then(() => add(value, scanned, entry)); queue.current = turn.catch(() => null); return turn;
  };

  async function choose(order) {
    const pending = choice;
    setChoice(null);
    start(order, await loadLaboratoryWorkflowVersion(order.workflow.versionId));
    const added = append(order, pending.collection, pending.scanned, pending.entry ?? null);
    if (station) station.announce(added);
  }

  // A scan on this page: a machine or a choice of the stage fills the shared
  // part; a tube joins the batch, or gets the cursor when it is already in it.
  useLabScan({ match: (scannedCode, kind) => {
    const current = live.current.batch;
    const target = current === null ? null : stageScanTarget(current.stage, scannedCode);
    if (target !== null) {
      return () => {
        const applied = applyStageScan(current.stage, target, live.current.shared, live.current.devices);
        const title = target.kind === 'device' ? target.device.name : itemLabel(target.item);
        if (!applied.ok) {
          return { tone: 'bad', title, detail: applied.reason === 'method_first' ? tx('ui.lab.stage.choose_method_first') : tx(`ui.lab.device.status.${target.device.status}`) };
        }
        setShared(applied.values); setDevices(applied.devices); setFlash(target.kind === 'device' ? 'device' : target.field.id);
        return { tone: 'ok', title, detail: target.kind === 'device' ? target.device.inventoryNumber : fieldLabel(target.field.code) };
      };
    }
    const inBatch = live.current.rows.some(row => row.order.tube.code === scannedCode || row.order.tube.printedCode === scannedCode);
    if (kind === 'text' && !inBatch) return null;
    return () => enqueue(scannedCode, true);
  } });

  const outcomeField = stage === null ? null : stage.fields.find(field => field.role === 'outcome');
  const methodField = stage === null ? null : stage.fields.find(field => field.role === 'method');
  const method = methodField ? shared[methodField.id] : null;
  const allowedDevices = stage === null ? [] : stage.deviceRule === 'per_method' ? stage.devices.filter(device => method && method.deviceIds.includes(device.id)) : stage.devices;
  const sharedFields = useMemo(() => (stage === null ? [] : stage.fields.filter(field => !perTube.includes(field.id)).sort((a, b) => a.position - b.position)), [stage, perTube]);
  const ownFields = useMemo(() => (stage === null ? [] : stage.fields.filter(field => perTube.includes(field.id)).sort((a, b) => a.position - b.position)), [stage, perTube]);

  function setSharedValue(field, value) {
    setShared((current) => {
      const next = { ...current, [field.id]: value };
      for (const child of stage.fields.filter(item => item.parentFieldId === field.id)) delete next[child.id];
      const average = readingsAverage(sharedFields, next, field);
      if (average !== null) next[average.field.id] = average.value;
      return next;
    });
  }
  function setRowValue(row, field, value) {
    update(row.key, (current) => {
      const values = { ...current.values, [field.id]: value };
      const average = readingsAverage(ownFields, values, field);
      if (average !== null) values[average.field.id] = average.value;
      return { values, state: ['failed', 'skipped'].includes(current.state) ? 'ready' : current.state, error: '' };
    });
  }
  function move(field) {
    const next = perTube.includes(field.id) ? perTube.filter(id => id !== field.id) : [...perTube, field.id];
    setPerTube(next);
    setShared((current) => { const copy = { ...current }; delete copy[field.id]; return copy; });
    live.current = { ...live.current, rows: live.current.rows.map((row) => { const values = { ...row.values }; delete values[field.id]; return { ...row, values }; }) };
    setRows(live.current.rows);
    try { window.localStorage.setItem(`${MEMORY}:${batch.workflow.code}:${stage.code}`, JSON.stringify(stage.fields.filter(item => next.includes(item.id)).map(item => item.code))); }
    catch (storage) { /* kept for this batch only */ }
  }
  const toggleDevice = id => setDevices(current => (stage.deviceRule === 'one_or_more'
    ? current.includes(id) ? current.filter(value => value !== id) : [...current, id] : [id]));

  const open = rows.filter(row => !['recorded', 'sent'].includes(row.state));
  const views = rows.map((row) => {
    const values = stage === null ? {} : rowValues(stage, shared, perTube, row);
    const failing = Boolean(outcomeField) && Boolean(values[outcomeField.id]) && values[outcomeField.id].outcome !== 'pass';
    return { row, values, failing, needs: stage === null ? [] : rowNeeds(stage, values) };
  });
  const recordable = views.filter(view => !['recorded', 'sent'].includes(view.row.state) && !view.failing && view.needs.length === 0);
  const done = rows.length - open.length;

  async function record() {
    if (busy || recordable.length === 0) return;
    setBusy(true); setError('');
    for (const view of views) {
      const { row } = view;
      if (['recorded', 'sent'].includes(row.state)) continue;
      if (view.failing) { update(row.key, { state: 'skipped', error: '' }); continue; }
      if (view.needs.length > 0) continue;
      const body = { workOrderUuid: row.order.uuid, expectedStageId: stage.id, values: payload(stage, view.values),
        deviceIds: devices.filter(id => allowedDevices.some(device => device.id === id)), ...(note.trim() ? { note } : {}),
        ...(row.scanned && station && station.scanner !== '' ? { scan: { sampleCode: row.order.tube.code, deviceId: station.scanner } } : {}) };
      const signature = JSON.stringify(body);
      const retry = row.retry !== null && row.retry.signature === signature ? row.retry : { signature, requestId: crypto.randomUUID() };
      update(row.key, { state: 'saving', retry, error: '' });
      try {
        await recordLaboratoryStage({ ...body, requestId: retry.requestId });
        update(row.key, { state: stage.approvalRequirement === 'required' ? 'sent' : 'recorded' });
      } catch (caught) { update(row.key, { state: 'failed', error: labError(caught) }); }
    }
    setBusy(false);
  }
  function clear() { live.current = { ...live.current, batch: null, rows: [] }; setBatch(null); setRows([]); setChoice(null); setShared({}); setDevices([]); setNote(''); setError(''); }

  return <div className="xo-products-page xo-batch-page">
    <Head title={text('title')} subtitle={text('subtitle')} />
    <div className="xo-receipt-bar">
      <form className="xo-receipt-scan" onSubmit={(event) => { event.preventDefault(); if (code.trim() !== '') { enqueue(code, false); setCode(''); } }}>
        <ScanLine aria-hidden="true" />
        <input value={code} onChange={event => setCode(event.target.value)} maxLength={64} placeholder={text('scan_placeholder')} aria-label={text('scan_placeholder')}
          data-scan-route="" autoComplete="off" spellCheck="false" />
        <button type="submit" className="xo-btn" disabled={code.trim() === ''}>{text('add')}</button>
      </form>
      <input ref={formInput} type="file" accept=".xlsx" hidden
        onChange={(event) => { const chosen = event.target.files?.[0]; event.target.value = ''; if (chosen) fillFromForm(chosen); }} />
      <button type="button" className="xo-btn-2 xo-batch-form" disabled={busy || reading} onClick={() => formInput.current?.click()}
        title={text('form_hint')}><FileSpreadsheet aria-hidden="true" />{text(reading ? 'form_reading' : 'form_fill')}</button>
    </div>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {filled !== null && <div className={`xo-alert ${filled.missed.length + filled.problems.length > 0 ? 't-warn' : 't-ok'}`} role="status">
      <strong>{text(filled.added === 1 ? 'form_filled_one' : 'form_filled', { count: filled.added })}</strong>
      {filled.missed.length > 0 && <p>{text('form_missed', { tubes: filled.missed.join(' · ') })}</p>}
      {filled.problems.length > 0 && <p>{filled.problems.join(' · ')}</p>}
    </div>}
    {choice !== null && <div className="xo-batch-choice" role="group" aria-label={text('which_workflow')}>
      <strong>{text('which_workflow')}</strong>
      {choice.orders.map(order => <button type="button" className="xo-btn-2" key={order.uuid} onClick={() => choose(order)}>
        <StageGlyph code={order.stages.find(item => item.id === order.currentStage.id).kind} />{order.workflow.code} · {stageName(order.currentStage.code)}</button>)}
    </div>}

    {batch === null ? choice === null && <div className="xo-receipt-empty xo-batch-empty"><Rows3 aria-hidden="true" /><strong>{text('empty')}</strong><p>{text('empty_hint')}</p></div> : <>
      <section className="xo-batch-stage" aria-label={stageName(stage.code)}>
        <header>
          <span className="xo-batch-stage-glyph"><StageGlyph code={stage.kind} /></span>
          <div><strong>{batch.workflow.code} · {stageName(stage.code)}</strong><span>{workflowName(batch.workflow.code)}</span></div>
          <Pill tone="flat">{text(rows.length === 1 ? 'tubes_one' : 'tubes', { count: rows.length })}</Pill>
          <button type="button" className="xo-btn-2" disabled={busy} onClick={clear}><X aria-hidden="true" />{text('new')}</button>
        </header>
        <h3><Layers aria-hidden="true" />{text('shared')}</h3>
        <div className="xo-stage-fields">{sharedFields.map(field => <div className="xo-batch-shared-field" key={field.id}>
          <FieldInput field={field} value={shared[field.id]} values={shared} disabled={busy} idPrefix="batch-shared" flash={flash === field.id}
            onChange={value => setSharedValue(field, value)} />
          <button type="button" className="xo-batch-move" title={text('make_per_tube')} aria-label={`${fieldLabel(field.code)} · ${text('make_per_tube')}`}
            disabled={busy} onClick={() => move(field)}><Rows3 aria-hidden="true" /></button>
        </div>)}</div>
        {stage.deviceRule !== 'none' && <fieldset className={`xo-stage-devices${flash === 'device' ? ' xo-scan-flash' : ''}`}>
          <legend>{tx(`ui.lab.stage.device_rule.${stage.deviceRule}`)}</legend>
          {stage.deviceRule === 'per_method' && !method ? <p>{tx('ui.lab.stage.choose_method_first')}</p>
            : allowedDevices.length === 0 ? <p>{tx('ui.lab.stage.method_without_device')}</p>
              : allowedDevices.map(device => <label key={device.id} className={`${devices.includes(device.id) ? 'is-active' : ''}${device.status !== 'in_service' ? ' is-disabled' : ''}`}>
                <input type={stage.deviceRule === 'one_or_more' ? 'checkbox' : 'radio'} name={`batch-devices-${stage.id}`} checked={devices.includes(device.id)}
                  disabled={busy || device.status !== 'in_service'} onChange={() => toggleDevice(device.id)} />
                <span><strong>{device.name}</strong><small>{device.inventoryNumber}</small></span>
                {device.status !== 'in_service' && <Pill tone="warn">{tx(`ui.lab.device.status.${device.status}`)}</Pill>}
              </label>)}
        </fieldset>}
        <label className="xo-stage-note"><span>{tx('ui.lab.stage.note')}</span>
          <textarea rows={2} maxLength={2000} value={note} disabled={busy} onChange={event => setNote(event.target.value)} /></label>
      </section>

      <section className="xo-batch-tubes" aria-label={text('per_tube')}>
        <div className="xo-batch-table-wrap"><table className="xo-batch-table">
          <thead><tr>
            <th scope="col">{text('tube')}</th>
            {ownFields.map(field => <th scope="col" key={field.id}>
              <span>{fieldLabel(field.code)}{field.unit && <small>{unitSymbol(field.unit)}</small>}{field.isRequired && <b className="xo-stage-required" aria-label={tx('ui.lab.stage.required')}>*</b>}</span>
              <button type="button" className="xo-batch-move" title={text('shared')} aria-label={`${fieldLabel(field.code)} · ${text('shared')}`} disabled={busy} onClick={() => move(field)}><Layers aria-hidden="true" /></button>
            </th>)}
            <th scope="col">{text('status')}</th><th aria-hidden="true" />
          </tr></thead>
          <tbody>{views.map(({ row, values, failing, needs }) => {
            const locked = busy || ['recorded', 'sent', 'saving'].includes(row.state);
            return <tr key={row.key} className={`is-${row.state}${flash === row.key ? ' xo-scan-flash' : ''}`}>
              <th scope="row"><span className="xo-batch-tube">
                <span className="xo-batch-tube-symbol"><ContainerSymbol code={row.container.code} /></span>
                <span><code>{row.order.tube.printedCode ?? row.order.tube.code}</code><small>{ownSampleTypeName(row.collection.sampleType)}</small></span>
                {row.scanned && <span className="xo-scan-mark" title={tx('ui.lab.scan.scanned')}><ScanLine aria-hidden="true" /></span>}
              </span></th>
              {ownFields.map(field => <td key={field.id} className={field.isRequired && empty(row.values[field.id]) ? 'is-needed' : ''}>
                <FieldInput bare field={field} value={row.values[field.id]} values={values} disabled={locked} idPrefix={`batch-${row.key}`}
                  onChange={value => setRowValue(row, field, value)} /></td>)}
              <td className="xo-batch-status">
                <Pill tone={STATES[row.state]}>{row.state === 'recorded' && <Check aria-hidden="true" />}{row.state === 'sent' && <ShieldCheck aria-hidden="true" />}
                  {text(row.state === 'ready' && failing ? 'skipped' : row.state)}</Pill>
                {row.state === 'failed' && <small className="is-bad">{row.error}</small>}
                {(row.state === 'skipped' || (row.state === 'ready' && failing)) && <small>{text('skipped_hint')}</small>}
                {row.state === 'ready' && !failing && needs.length > 0 && <small>{text('needs', { fields: needs.map(field => fieldLabel(field.code)).join(', ') })}</small>}
              </td>
              <td><button type="button" className="xo-icon-btn" aria-label={text('remove', { code: row.order.tube.printedCode ?? row.order.tube.code })} disabled={locked}
                onClick={() => { live.current = { ...live.current, rows: live.current.rows.filter(item => item.key !== row.key) }; setRows(live.current.rows); }}><X aria-hidden="true" /></button></td>
            </tr>;
          })}</tbody>
        </table></div>
        <footer>
          <span role="status">{done > 0 ? text('summary', { done, total: rows.length }) : ''}</span>
          <button type="button" className="xo-btn" disabled={busy || recordable.length === 0} onClick={record}>
            {stage.approvalRequirement === 'required' ? <ShieldCheck aria-hidden="true" /> : <Check aria-hidden="true" />}
            {busy ? tx('ui.lab.stage.saving') : text(recordable.length === 1 ? 'record_one' : 'record', { count: recordable.length })}</button>
        </footer>
      </section>
    </>}
  </div>;
}
